import {
  fingerprintPrivateSummary,
  type AudienceAssignment,
  type Bookmark,
  type LibraryFormat,
  type LibraryLane,
  type LibraryReceipt,
  type LibraryStatus,
} from "./domain";

export const demoAudiences = [
  { id: "maya", label: "Maya", hue: "rose" },
  { id: "phelim", label: "Phelim", hue: "mint" },
  { id: "me", label: "Just me", hue: "butter" },
  { id: "parents", label: "Parents", hue: "violet" },
] as const;

export const demoViewerUserID = "mark-user";

export const demoUserLabels: Readonly<Record<string, string>> = {
  mark: "Mark",
  maya: "Maya",
  phelim: "Phelim",
  alice: "Alice",
};

const collectedDemoReceipts: LibraryReceipt[] = [];
let bookmarkFixtureOrdinal = 0;

function assignment(
  audienceId: string,
  conversationEnabled: boolean,
  libraryEnabled: boolean,
  conversationState: AudienceAssignment["conversationState"] = "available",
): AudienceAssignment {
  const audience = demoAudiences.find(({ id }) => id === audienceId);
  if (!audience) throw new Error(`Unknown demo audience ${audienceId}`);
  return {
    id: "",
    audienceId,
    audienceLabel: audience.label,
    conversationEnabled,
    libraryEnabled,
    isPrimary: false,
    conversationState,
  };
}

type DemoBookmarkInput = Omit<
  Bookmark,
  | "audienceAssignments"
  | "libraryPublications"
  | "format"
  | "lane"
  | "summaryFingerprint"
  | "embeddedSummaryFingerprint"
  | "vectorStatus"
> & {
  audienceAssignments: AudienceAssignment[];
  sharedTitle: string;
  sharedNotes?: string;
  lane: LibraryLane;
  contentFormat?: LibraryFormat;
  status: LibraryStatus;
  publicationOverrides?: Partial<Record<string, {
    sharedTitle?: string;
    sharedNotes?: string;
    sharedSourceLabel?: string;
    sharedURL?: string | null;
    format?: LibraryFormat;
    lane?: LibraryLane;
    videoDurationSeconds?: number;
  }>>;
};

function bookmark(
  value: DemoBookmarkInput,
  vectorStatus: Bookmark["vectorStatus"] = "ready",
): Bookmark {
  bookmarkFixtureOrdinal += 1;
  const summaryFingerprint = fingerprintPrivateSummary(value.privateHumanSummary);
  const audienceAssignments = value.audienceAssignments.map(
    (candidate, index) => ({
      ...candidate,
      id: `assignment-${bookmarkFixtureOrdinal}-${candidate.audienceId}`,
      isPrimary: index === 0,
    }),
  );
  const format = value.contentFormat ?? formatForLane(value.lane);
  const libraryPublications = audienceAssignments.flatMap((candidate) => {
    if (!candidate.libraryEnabled) return [];
    const override = value.publicationOverrides?.[candidate.audienceId];
    const publication = {
      id: `publication-${bookmarkFixtureOrdinal}-${candidate.audienceId}`,
      assignmentId: candidate.id,
      sharedByUserID: value.senderID,
      sharedTitle: override?.sharedTitle ?? value.sharedTitle,
      sharedURL: override?.sharedURL === undefined
        ? value.sourceURL
        : override.sharedURL,
      sharedSourceLabel: override?.sharedSourceLabel ?? value.sourceLabel,
      sharedNotes: override?.sharedNotes
        ?? value.sharedNotes
        ?? `Shared with ${candidate.audienceLabel} for later.`,
      publishedAt: value.createdAt,
      updatedAt: value.createdAt,
      revokedAt: null,
      format: override?.format ?? format,
      lane: override?.lane ?? value.lane,
      videoDurationSeconds: override?.videoDurationSeconds
        ?? value.videoDurationSeconds,
    };
    collectedDemoReceipts.push(fixtureReceipt(
      publication.id,
      value.status,
      value.createdAt,
    ));
    return [publication];
  });
  const {
    sharedTitle: _sharedTitle,
    sharedNotes: _sharedNotes,
    status: _status,
    contentFormat: _contentFormat,
    publicationOverrides: _publicationOverrides,
    ...bookmarkValue
  } = value;
  return {
    ...bookmarkValue,
    cluster: value.cluster ?? clusterForTheme(value.primaryTheme),
    audienceAssignments,
    libraryPublications,
    format,
    lane: value.lane,
    privateSummaryInputMode: value.privateHumanSummary.trim()
      ? "typed"
      : null,
    triageAudit: null,
    summaryFingerprint,
    embeddedSummaryFingerprint: vectorStatus === "ready"
      ? summaryFingerprint
      : null,
    vectorStatus,
  };
}

function fixtureReceipt(
  publicationId: string,
  state: LibraryStatus,
  updatedAt: string,
): LibraryReceipt {
  const base = {
    publicationId,
    userId: demoViewerUserID,
    state,
    updatedAt,
  };
  switch (state) {
    case "new":
      return base;
    case "open":
      return { ...base, openedAt: updatedAt };
    case "in_progress":
      return {
        ...base,
        openedAt: updatedAt,
        progressUpdatedAt: updatedAt,
      };
    case "finished":
      return {
        ...base,
        openedAt: updatedAt,
        finishedAt: updatedAt,
      };
    case "dismissed":
      return { ...base, dismissedAt: updatedAt };
  }
}

function formatForLane(lane: LibraryLane): LibraryFormat {
  switch (lane) {
    case "listen": return "audio";
    case "watch": return "video";
    default: return "text";
  }
}

function clusterForTheme(theme: string): string {
  switch (theme) {
    case "Togetherness":
    case "Listening":
    case "Family":
    case "Perspective":
      return "relationship-rhythms-and-intimacy";
    case "Home":
      return "housing-and-lifestyle-tradeoffs";
    case "Adventure":
    case "Dreaming":
      return "travel-service-and-cultural-phrases";
    case "Curiosity":
    case "Making":
    case "Play":
      return "growth-confidence-and-agency";
    case "Stories":
    case "Memory":
    case "Soundtrack":
      return "art-media-and-humor";
    default:
      return "unclassified-social-saves";
  }
}

export const demoBookmarks: Bookmark[] = [
  bookmark({
    id: "demo-read-maya-01",
    title: "Why small rituals make conversations easier",
    sharedTitle: "Why small rituals make conversations easier",
    sharedNotes: "A gentle opener I thought Maya would enjoy.",
    sourceLabel: "Field Notes",
    sourceURL: "https://example.com/rituals",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [
      assignment("maya", true, true),
      assignment("phelim", true, false),
    ],
    lane: "read",
    status: "new",
    createdAt: "2026-07-25T16:20:00Z",
    durationLabel: "7 min read",
    primaryTheme: "Togetherness",
    privateNotes: "What tiny ritual should we steal for our week?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: essay about low-pressure rituals and conversation openings.",
    keywords: ["rituals", "conversation", "relationships"],
    tags: ["relationships", "slow living"],
    privateCardMedia: [{
      kind: "image",
      url: "/demo/shared-morning-ritual.jpg",
      alt: "Two people holding handmade cups across a sunlit kitchen table.",
    }],
  }),
  bookmark({
    id: "demo-listen-maya-02",
    title: "The science of listening without fixing",
    sharedTitle: "The science of listening without fixing",
    sourceLabel: "A Curious Life",
    sourceURL: "https://example.com/listening",
    senderID: "maya",
    senderLabel: "Maya",
    audienceAssignments: [
      assignment("maya", true, true),
      assignment("phelim", false, true),
    ],
    publicationOverrides: {
      phelim: {
        sharedTitle: "Listening well without rushing to fix it",
        sharedNotes: "The practical middle section made me think of you.",
      },
    },
    lane: "listen",
    status: "in_progress",
    createdAt: "2026-07-24T09:30:00Z",
    durationLabel: "42 min",
    primaryTheme: "Listening",
    privateNotes: "When do we want advice, and when do we only want company?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: interview about reflective listening and resisting advice.",
    keywords: ["listening", "empathy", "advice"],
    tags: ["podcast", "relationships"],
  }),
  bookmark({
    id: "demo-watch-maya-03",
    title: "A tiny apartment transformed by light",
    sharedTitle: "A tiny apartment transformed by light",
    sourceLabel: "Open House",
    sourceURL: "https://example.com/apartment-light",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [
      assignment("maya", true, true),
      assignment("me", false, true),
    ],
    lane: "watch",
    status: "finished",
    createdAt: "2026-07-22T18:15:00Z",
    durationLabel: "12 min",
    videoDurationSeconds: 12 * 60,
    primaryTheme: "Home",
    privateNotes: "Would hidden shelf light make our evenings feel calmer?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: tour focused on layered warm lighting in a compact home.",
    keywords: ["lighting", "apartment", "interior design"],
    tags: ["home", "video"],
  }),
  bookmark({
    id: "demo-read-me-04",
    title: "A practical field guide to making better notes",
    sharedTitle: "A practical field guide to making better notes",
    sourceLabel: "Commonplace",
    sourceURL: "https://example.com/notes",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("me", false, true)],
    lane: "read",
    status: "open",
    createdAt: "2026-07-21T13:10:00Z",
    durationLabel: "9 min read",
    primaryTheme: "Curiosity",
    privateNotes: "Try the three-pass review next week.",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: actionable system for capture, compression, and retrieval.",
    keywords: ["notes", "review", "knowledge"],
    tags: ["self-development"],
  }),
  bookmark({
    id: "demo-listen-parents-05",
    title: "The long history of the Sunday roast",
    sharedTitle: "The long history of the Sunday roast",
    sourceLabel: "Table Stories",
    sourceURL: "https://example.com/sunday-roast",
    senderID: "alice",
    senderLabel: "Alice",
    audienceAssignments: [
      assignment("parents", true, true),
      assignment("phelim", true, false, "postponed"),
    ],
    lane: "listen",
    status: "new",
    createdAt: "2026-07-19T08:45:00Z",
    durationLabel: "28 min",
    primaryTheme: "Family",
    privateNotes: "Which family meal do we remember most differently?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: food-history episode about regional family traditions.",
    keywords: ["food history", "family", "Britain"],
    tags: ["food", "family"],
  }),
  bookmark({
    id: "demo-watch-parents-06",
    title: "Restoring a century-old coastal path",
    sharedTitle: "Restoring a century-old coastal path",
    sourceLabel: "Public Works",
    sourceURL: "https://example.com/coastal-path",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("parents", true, true)],
    lane: "watch",
    status: "new",
    createdAt: "2026-07-17T20:00:00Z",
    durationLabel: "18 min",
    videoDurationSeconds: 18 * 60,
    primaryTheme: "Adventure",
    privateNotes: "Where should our next long walk be?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: documentary short about trail repair and community stewardship.",
    keywords: ["walking", "restoration", "coast"],
    tags: ["travel", "nature"],
  }),
  bookmark({
    id: "demo-read-phelim-07",
    title: "The strange pleasure of a terrible first draft",
    sharedTitle: "The strange pleasure of a terrible first draft",
    sourceLabel: "Working Notes",
    sourceURL: "https://example.com/first-drafts",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("phelim", true, true)],
    lane: "read",
    status: "open",
    createdAt: "2026-07-16T12:00:00Z",
    durationLabel: "6 min read",
    primaryTheme: "Making",
    privateNotes: "What project would be more fun if we let it be bad first?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: permission to use rough drafts as a route to original work.",
    keywords: ["creativity", "drafting", "perfectionism"],
    tags: ["work", "making"],
  }),
  bookmark({
    id: "demo-watch-phelim-08",
    title: "Building a tiny synth from spare parts",
    sharedTitle: "Building a tiny synth from spare parts",
    sourceLabel: "Bench Lab",
    sourceURL: "https://example.com/tiny-synth",
    senderID: "phelim",
    senderLabel: "Phelim",
    audienceAssignments: [assignment("phelim", true, true)],
    lane: "watch",
    status: "new",
    createdAt: "2026-07-15T18:30:00Z",
    durationLabel: "3 min",
    videoDurationSeconds: 3 * 60,
    primaryTheme: "Play",
    privateNotes: "What delightfully unnecessary thing should we build?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: playful hardware build using recycled electronics.",
    keywords: ["synth", "hardware", "play"],
    tags: ["making", "video"],
  }),
  bookmark({
    id: "demo-listen-maya-09",
    title: "What the city sounds like before breakfast",
    sharedTitle: "What the city sounds like before breakfast",
    sourceLabel: "Sidewalk Radio",
    sourceURL: "https://example.com/city-dawn",
    senderID: "maya",
    senderLabel: "Maya",
    audienceAssignments: [assignment("maya", true, true, "discussed")],
    lane: "listen",
    status: "finished",
    createdAt: "2026-07-14T07:15:00Z",
    durationLabel: "24 min",
    primaryTheme: "Wonder",
    privateNotes: "What hour of the city feels most like ours?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: field recordings and stories from cities around sunrise.",
    keywords: ["cities", "sound", "morning"],
    tags: ["audio", "place"],
  }),
  bookmark({
    id: "demo-read-maya-10",
    title: "The questions people keep for long train rides",
    sharedTitle: "The questions people keep for long train rides",
    sourceLabel: "Window Seat",
    sourceURL: "https://example.com/train-questions",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "read",
    status: "new",
    createdAt: "2026-07-13T21:40:00Z",
    durationLabel: "5 min read",
    primaryTheme: "Dreaming",
    privateNotes: "Where would we go if the journey mattered more than arriving?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: intimate travel prompts collected on night trains.",
    keywords: ["travel", "questions", "trains"],
    tags: ["dreaming"],
  }),
  bookmark({
    id: "demo-watch-maya-11",
    title: "One garden through four seasons",
    sharedTitle: "One garden through four seasons",
    sourceLabel: "Quiet Frames",
    sourceURL: "https://example.com/four-seasons",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "watch",
    status: "new",
    createdAt: "2026-07-12T16:05:00Z",
    durationLabel: "11 min",
    videoDurationSeconds: 11 * 60,
    primaryTheme: "Change",
    privateNotes: "What have we learned to notice this year?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: time-lapse portrait of a community garden across one year.",
    keywords: ["seasons", "garden", "change"],
    tags: ["nature", "video"],
  }),
  bookmark({
    id: "demo-listen-maya-12",
    title: "How jokes become family language",
    sharedTitle: "How jokes become family language",
    sourceLabel: "Between Us",
    sourceURL: "https://example.com/family-jokes",
    senderID: "maya",
    senderLabel: "Maya",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "listen",
    status: "new",
    createdAt: "2026-07-11T10:25:00Z",
    durationLabel: "31 min",
    primaryTheme: "Stories",
    privateNotes: "Which of our private jokes deserves an origin story?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: oral-history episode about jokes that bind families together.",
    keywords: ["humor", "family", "language"],
    tags: ["stories", "audio"],
  }),
  bookmark({
    id: "demo-read-maya-13",
    title: "A map made entirely of remembered smells",
    sharedTitle: "A map made entirely of remembered smells",
    sourceLabel: "Odd Atlas",
    sourceURL: "https://example.com/smell-map",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "read",
    status: "new",
    createdAt: "2026-07-10T14:10:00Z",
    durationLabel: "8 min read",
    primaryTheme: "Memory",
    privateNotes: "What smell sends each of us somewhere instantly?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: artists map cities using residents' scent memories.",
    keywords: ["memory", "smell", "cities"],
    tags: ["art", "place"],
  }),
  bookmark({
    id: "demo-watch-maya-14",
    title: "A chef cooks the meal she missed most",
    sharedTitle: "A chef cooks the meal she missed most",
    sourceLabel: "Home Table",
    sourceURL: "https://example.com/missed-meal",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "watch",
    status: "new",
    createdAt: "2026-07-09T19:50:00Z",
    durationLabel: "14 min",
    videoDurationSeconds: 14 * 60,
    primaryTheme: "Belonging",
    privateNotes: "What meal would each of us recreate for the other?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: chef reconstructs a childhood dish from sensory memory.",
    keywords: ["food", "home", "memory"],
    tags: ["belonging", "video"],
  }),
  bookmark({
    id: "demo-read-maya-15",
    title: "The case for leaving one day unplanned",
    sharedTitle: "The case for leaving one day unplanned",
    sourceLabel: "Open Calendar",
    sourceURL: "https://example.com/unplanned",
    senderID: "maya",
    senderLabel: "Maya",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "read",
    status: "new",
    createdAt: "2026-07-08T08:30:00Z",
    durationLabel: "4 min read",
    primaryTheme: "Spontaneity",
    privateNotes: "If Saturday were blank, where might it take us?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: essay about protecting unscheduled time for shared surprise.",
    keywords: ["time", "spontaneity", "rest"],
    tags: ["slow living"],
  }),
  bookmark({
    id: "demo-listen-maya-16",
    title: "The songs that changed shape as we did",
    sharedTitle: "The songs that changed shape as we did",
    sourceLabel: "Second Verse",
    sourceURL: "https://example.com/changing-songs",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "listen",
    status: "new",
    createdAt: "2026-07-07T17:40:00Z",
    durationLabel: "27 min",
    primaryTheme: "Soundtrack",
    privateNotes: "Which song means something completely different to us now?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: people revisit songs whose meaning changed with their lives.",
    keywords: ["music", "memory", "change"],
    tags: ["audio", "stories"],
  }),
  bookmark({
    id: "demo-read-maya-17",
    title: "Two people draw the same remembered room",
    sharedTitle: "Two people draw the same remembered room",
    sourceLabel: "Memory Palace",
    sourceURL: "https://example.com/remembered-room",
    senderID: "maya",
    senderLabel: "Maya",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "read",
    status: "new",
    createdAt: "2026-07-06T11:15:00Z",
    durationLabel: "7 min read",
    primaryTheme: "Perspective",
    privateNotes: "Which place from our story would we draw most differently?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: paired drawings reveal how memory edits shared spaces.",
    keywords: ["memory", "drawing", "perspective"],
    tags: ["art", "relationships"],
  }),
  bookmark({
    id: "demo-watch-maya-18",
    title: "A midnight bakery opens its doors",
    sharedTitle: "A midnight bakery opens its doors",
    sourceLabel: "Night Shift",
    sourceURL: "https://example.com/midnight-bakery",
    senderID: "mark",
    senderLabel: "Mark",
    audienceAssignments: [assignment("maya", true, false)],
    lane: "watch",
    status: "new",
    createdAt: "2026-07-05T22:05:00Z",
    durationLabel: "13 min",
    videoDurationSeconds: 13 * 60,
    primaryTheme: "Small Hours",
    privateNotes: "What secret nighttime world would we like to visit together?",
    privateHumanSummary: "PRIVATE-DEMO-MARKER: quiet portrait of bakers preparing a city before dawn.",
    keywords: ["night", "craft", "food"],
    tags: ["video", "wonder"],
  }),
];

const firstMayaPublication = demoBookmarks[0].libraryPublications.find(
  ({ assignmentId }) => assignmentId === "assignment-1-maya",
);

export const demoLibraryReceipts: LibraryReceipt[] = [
  ...collectedDemoReceipts,
  ...(firstMayaPublication
    ? [{
      publicationId: firstMayaPublication.id,
      userId: "maya-user",
      state: "open" as const,
      openedAt: "2026-07-25T16:25:00Z",
      updatedAt: "2026-07-25T16:25:00Z",
    }]
    : []),
];
